import { useState, useEffect, useMemo } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PillTabs, TableChip } from '../../components/PremiumDesign';
import { V2Table } from '@/common/components';
import {
  Button,
  ConfirmDeleteModal,
  CustomSelect,
  SearchInput,
} from '@/components';
import { PlusIcon } from '../../components/Icons';
import editIcon from '../../assets/editIcon.png';
import trashIcon from '../../assets/trashIcon.png';
import {
  getCRMCustomFields,
  createCRMCustomField,
  deleteCRMCustomField,
  updateCRMCustomField,
} from '../api';
import { parseApiError } from '@/common/utils';
import { useDelayedSkeleton } from '@/common/hooks';

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown (Select)' },
  { value: 'boolean', label: 'Yes/No (Boolean)' },
];

export function CustomFieldsTab({
  companyId,
  isReadOnly = false,
  canCreate = false,
  canUpdate = false,
  canDelete = false,
  tabs,
  activeTab,
  onTabChange,
}) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [previewingField, setPreviewingField] = useState(null);
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [fieldOptions, setFieldOptions] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [apiErrors, setApiErrors] = useState({});
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const isEditMode = !!editingField;
  const isPreviewMode = !!previewingField && !isEditMode;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const {
    data: fieldsResponse,
    isLoading: isInitialLoading,
    isFetching,
  } = useQuery({
    queryKey: ['crm-custom-fields', companyId, page, debouncedSearchQuery],
    queryFn: () =>
      getCRMCustomFields(companyId, { page, search: debouncedSearchQuery }),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && fieldsResponse !== undefined,
    300,
    500,
  );
  const isLoading = isInitialLoading || showDelayedSkeleton;

  const customFields = fieldsResponse?.results || fieldsResponse || [];
  const totalCount = fieldsResponse?.count || 0;

  const resetForm = () => {
    setEditingField(null);
    setPreviewingField(null);
    setFieldName('');
    setFieldType('text');
    setFieldOptions('');
    setIsRequired(false);
    setDisplayOrder(0);
    setApiErrors({});
  };

  const createMutation = useMutation({
    mutationFn: (payload) => {
      if (!canCreate) throw new Error('Permission denied.');
      return createCRMCustomField({ companyId, payload });
    },
    onSuccess: () => {
      toast.success('Custom field created successfully!');
      queryClient.invalidateQueries(['crm-custom-fields', companyId]);
      resetForm();
    },
    onError: (err) => {
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      toast.error(message || 'Failed to create custom field.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ fieldId, payload }) => {
      if (!canUpdate) throw new Error('Permission denied.');
      return updateCRMCustomField({ companyId, fieldId, payload });
    },
    onSuccess: () => {
      toast.success('Custom field updated successfully!');
      queryClient.invalidateQueries(['crm-custom-fields', companyId]);
      resetForm();
    },
    onError: (err) => {
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      toast.error(message || 'Failed to update custom field.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (fieldId) => {
      if (!canDelete) throw new Error('Permission denied.');
      return deleteCRMCustomField({ companyId, fieldId });
    },
    onSuccess: () => {
      toast.success('Custom field deleted successfully!');
      queryClient.invalidateQueries(['crm-custom-fields', companyId]);
      setFieldToDelete(null);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete custom field.');
      setFieldToDelete(null);
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const startEditing = (field) => {
    setEditingField(field);
    setPreviewingField(null);
    setFieldName(field.name || '');
    setFieldType(field.field_type || 'text');
    setFieldOptions(
      Array.isArray(field.options)
        ? field.options.join(', ')
        : field.options || '',
    );
    setIsRequired(field.is_required || false);
    setDisplayOrder(field.display_order || 0);
    setApiErrors({});
  };

  const startPreview = (field) => {
    setPreviewingField(field);
    setEditingField(null);
    setFieldName(field.name || '');
    setFieldType(field.field_type || 'text');
    setFieldOptions(
      Array.isArray(field.options)
        ? field.options.join(', ')
        : field.options || '',
    );
    setIsRequired(field.is_required || false);
    setDisplayOrder(field.display_order || 0);
    setApiErrors({});
  };

  const handleSubmit = () => {
    if (!fieldName.trim() || isReadOnly || isSaving) return;

    if (fieldType === 'select' && !fieldOptions.trim()) {
      toast.error('Options are required for Select field type.');
      return;
    }

    const payload = {
      name: fieldName.trim(),
      slug: fieldName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, ''),
      field_type: fieldType,
      is_required: isRequired,
      display_order: parseInt(displayOrder, 10) || 0,
    };

    if (fieldType === 'select') {
      payload.options = fieldOptions
        .split(',')
        .map((o) => o.trim())
        .filter(Boolean);
    }

    if (editingField) {
      const fieldId = editingField.id;
      if (fieldId) {
        updateMutation.mutate({ fieldId, payload });
      } else {
        toast.error('Editing not supported for this field structure.');
      }
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDeleteClick = (field) => {
    const fieldId = field.id;
    if (fieldId) {
      setFieldToDelete(field);
    } else {
      toast.error('Deleting not supported for this field structure.');
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
        defaultWidth: 240,
        minWidth: 160,
        cell: (field) => (
          <span className="truncate block font-medium" title={field.name}>
            {field.name}
          </span>
        ),
      },
      {
        id: 'type',
        label: 'Type',
        defaultWidth: 160,
        minWidth: 110,
        cell: (field) => <span className="capitalize">{field.field_type}</span>,
      },
      {
        id: 'required',
        label: 'Required',
        defaultWidth: 120,
        minWidth: 90,
        align: 'center',
        cell: (field) => (
          <TableChip tone={field.is_required ? 'success' : 'neutral'}>
            {field.is_required ? 'Yes' : 'No'}
          </TableChip>
        ),
      },
      {
        id: 'actions',
        label: 'Action',
        defaultWidth: 120,
        minWidth: 96,
        align: 'center',
        stopPropagation: true,
        cell: (field) => (
          <div className="flex items-center justify-center gap-3">
            {canUpdate && (
              <button
                type="button"
                onClick={() => startEditing(field)}
                className="hover:opacity-80 transition-opacity shrink-0"
                title="Edit Field"
              >
                <img
                  src={editIcon}
                  alt=""
                  className="w-5 h-5 object-contain shrink-0"
                />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => handleDeleteClick(field)}
                className="hover:opacity-80 transition-opacity shrink-0"
                title="Delete Field"
              >
                <img
                  src={trashIcon}
                  alt=""
                  className="w-5 h-5 object-contain shrink-0"
                />
              </button>
            )}
          </div>
        ),
      },
    ],
    [canUpdate, canDelete],
  );

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Create/Edit Field form header - Fixed */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0 mb-4">
        <h4 className="text-[22px] leading-[28px] font-bold font-sf-pro-display text-[#1F2241]">
          {editingField
            ? 'Edit Field'
            : isPreviewMode
              ? 'Field Preview'
              : 'Create Field'}
        </h4>
        {tabs && (
          <PillTabs items={tabs} value={activeTab} onChange={onTabChange} />
        )}
      </div>

      {/* Lower content - Full Scroll */}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar flex flex-col gap-4 pb-8 pr-1">
        <div className="bg-form-background-light rounded-[16px] p-4 shrink-0">
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-8">
            <div className="flex flex-col gap-2 flex-1 min-w-0 max-w-md">
              <label className="text-sm font-medium text-text-secondary">
                Field Name<span className="text-status-error">*</span>
              </label>
              <input
                type="text"
                value={fieldName}
                onChange={(e) => {
                  setFieldName(e.target.value);
                  setApiErrors((prev) => ({ ...prev, name: null }));
                }}
                placeholder="e.g. Industry, Employee Count"
                disabled={isReadOnly || isSaving || isPreviewMode}
                className={`w-full h-11 px-4 rounded-xl bg-white border text-text-primary text-sm placeholder:text-text-muted focus:outline-none transition-colors ${apiErrors?.name ? 'border-status-error focus:border-status-error' : 'border-border-default focus:border-primary'} ${isReadOnly || isPreviewMode ? 'opacity-70 cursor-not-allowed' : ''}`}
              />
              {apiErrors?.name && (
                <p className="text-status-error text-xs font-medium">
                  {apiErrors.name}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0 min-w-[200px]">
              <label className="text-sm font-medium text-text-secondary">
                Field Type<span className="text-status-error">*</span>
              </label>
              <CustomSelect
                options={FIELD_TYPE_OPTIONS}
                value={fieldType}
                onChange={setFieldType}
                disabled={isReadOnly || isSaving || isPreviewMode}
                className="w-full sm:w-[220px] h-11"
                buttonClassName="w-full h-full flex items-center justify-between rounded-xl bg-white border border-border-default text-text-primary text-sm focus:outline-none focus:border-primary px-4"
              />
            </div>
            {fieldType === 'select' && (
              <div className="flex flex-col gap-2 flex-1 min-w-0 max-w-md">
                <label className="text-sm font-medium text-text-secondary">
                  Options (comma separated)
                  <span className="text-status-error">*</span>
                </label>
                <input
                  type="text"
                  value={fieldOptions}
                  onChange={(e) => setFieldOptions(e.target.value)}
                  placeholder="e.g. Option 1, Option 2, Option 3"
                  disabled={isReadOnly || isSaving || isPreviewMode}
                  className={`w-full h-11 px-4 rounded-xl bg-white border text-text-primary text-sm placeholder:text-text-muted focus:outline-none transition-colors border-border-default focus:border-primary ${isReadOnly || isPreviewMode ? 'opacity-70 cursor-not-allowed' : ''}`}
                />
                <span className="text-xs text-text-muted">
                  Separate multiple options with commas.
                </span>
              </div>
            )}
            <div className="flex flex-col gap-2 shrink-0">
              <label className="text-sm font-medium text-text-secondary">
                Required Field
              </label>
              <div className="h-11 flex items-center gap-[16px]">
                {[
                  { value: false, label: 'No' },
                  { value: true, label: 'Yes' },
                ].map((option) => {
                  const isSelected = isRequired === option.value;
                  return (
                    <label
                      key={option.label}
                      className={`flex items-center gap-2 ${isReadOnly || isSaving || isPreviewMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span
                        className={`relative w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'border-primary'
                            : 'border-border-default'
                        }`}
                      >
                        {isSelected && (
                          <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                        )}
                      </span>
                      <input
                        type="radio"
                        name="isRequired"
                        checked={isSelected}
                        disabled={isReadOnly || isSaving || isPreviewMode}
                        onChange={() => setIsRequired(option.value)}
                        className="sr-only"
                      />
                      <span
                        className={`text-sm font-medium ${isSelected ? 'text-text-primary' : 'text-text-muted'}`}
                      >
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full shrink-0">
          <SearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search custom fields by name"
            className="w-full sm:max-w-md sm:flex-1 order-2 sm:order-1"
            inputClassName="h-[44px]"
          />

          {isEditMode ? (
            <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 sm:ml-auto order-1 sm:order-2">
              <Button
                variant="secondary"
                onClick={resetForm}
                disabled={isSaving}
                className="h-[44px] px-4 flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSaving || !fieldName.trim()}
                className="h-[44px] px-4 flex-1 sm:flex-none"
              >
                {isSaving ? 'Saving...' : 'Save Change'}
              </Button>
            </div>
          ) : isPreviewMode ? (
            <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 sm:ml-auto order-1 sm:order-2">
              <Button
                variant="secondary"
                onClick={resetForm}
                className="h-[44px] px-4 flex-1 sm:flex-none"
              >
                Close
              </Button>
              {canUpdate && (
                <Button
                  variant="primary"
                  onClick={() => startEditing(previewingField)}
                  className="h-[44px] px-4 flex-1 sm:flex-none"
                >
                  Edit Field
                </Button>
              )}
            </div>
          ) : (
            canCreate && (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSaving || isReadOnly || !fieldName.trim()}
                className="h-[44px] px-4 w-full sm:w-auto shrink-0 sm:ml-auto order-1 sm:order-2"
                icon={<PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
              >
                <span className="whitespace-nowrap">
                  {isSaving ? 'Adding...' : 'Add Field'}
                </span>
              </Button>
            )
          )}
        </div>

        {/* Table */}
        <div className="w-full min-w-0 flex flex-col flex-1 min-h-0">
          <V2Table
            tight
            storageKey="crm_custom_fields_table_column_widths"
            columns={columns}
            data={Array.isArray(customFields) ? customFields : []}
            rowKey={(field, index) => field.id || index}
            loading={isLoading || !fieldsResponse}
            onRowClick={(field) => startPreview(field)}
            isRowSelected={(field) =>
              (editingField && editingField.id === field.id) ||
              (previewingField && previewingField.id === field.id)
            }
            empty="No custom fields found"
            pagination={{
              currentPage: page,
              totalCount,
              itemsPerPage: 10,
              onChange: setPage,
            }}
          />
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={!!fieldToDelete}
        title="Delete Custom Field"
        description="This custom field and any associated data will be permanently deleted."
        onConfirm={() => deleteMutation.mutate(fieldToDelete.id)}
        onCancel={() => setFieldToDelete(null)}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
